// Det i UR der ikke kommer fra vMix-titlen: rundemarkøren og medical break,
// der lægger sig over midterboksen. Begge sidder i UR's lag, så de følger
// med når baren flyver ind og ud.

import { useEffect, useMemo, useRef } from "react";
import medicalSpec from "./specs/medical.json";
import type { GtSpec } from "./spec.js";
import { StagePlayer } from "./player.js";
import { Stage } from "./Stage.js";
import { clock, roundOf, timerRemaining, timerState, type GfxCtx } from "./ctx.js";
import { StrikesOverlay } from "./spotter/SpotterGraphics.js";

// Målt i vMix-titlen: streger 5×37, boksen 39×37, første plads x=1068,5.
const TOP = 914.5;
const HEIGHT = 37;
const BAR = 5;
const BOX = 39;
const GAP_BARS = 9.5;
const GAP_BOX = 7;
const LEFT = 1068.5;
// Højre kant der ikke må overskrides (midterboksen slutter ved 1171,5).
const RIGHT_LIMIT = 1150;
// Kommende runder er dæmpede streger.
const UPCOMING_OPACITY = 0.45;

function RoundMarks({ current, total }: { current: number; total: number }) {
  const slots = Array.from({ length: total }, (_, i) => i + 1);
  const width = (n: number) => (n === current ? BOX : BAR);
  const gap = (a: number, b: number) => (a === current || b === current ? GAP_BOX : GAP_BARS);
  const span = slots.reduce((sum, n, i) => sum + width(n) + (i ? gap(slots[i - 1]!, n) : 0), 0);
  let x = Math.min(LEFT, RIGHT_LIMIT - span);
  return (
    <>
      {slots.map((n, i) => {
        if (i) x += gap(slots[i - 1]!, n);
        const left = x;
        x += width(n);
        if (n === current) {
          // Boks og tekst som i vMix-titlen (R2 og RoundTekst): teksten sidder
          // 4 px inde og 1 px nede i en 31 px bred kasse.
          return (
            <div key={n}>
              <div style={{ position: "absolute", left, top: TOP, width: BOX, height: HEIGHT, background: "#FFFFFF" }} />
              <div
                style={{
                  position: "absolute",
                  left: left + 4,
                  top: TOP + 1,
                  width: 31,
                  height: HEIGHT,
                  color: "#000000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: '"Bebas Neue", sans-serif',
                  fontSize: 36,
                  lineHeight: "normal",
                  whiteSpace: "pre",
                }}
              >
                R{n}
              </div>
            </div>
          );
        }
        return (
          <div
            key={n}
            style={{
              position: "absolute",
              left,
              top: TOP,
              width: BAR,
              height: HEIGHT,
              background: "#FFFFFF",
              opacity: n > current ? UPCOMING_OPACITY : 1,
            }}
          />
        );
      })}
    </>
  );
}

// Medical Break-titlen har præcis samme mål og placering som UR's midterboks.
function MedicalOverlay({ on, text }: { on: boolean; text: string }) {
  const spec = medicalSpec as unknown as GtSpec;
  const player = useMemo(() => new StagePlayer(spec), [spec]);
  const seen = useRef<boolean | null>(null);
  useEffect(() => {
    const before = seen.current;
    seen.current = on;
    if (before === null) {
      if (on) player.show(["in"]);
    } else if (on && !before) player.play("in");
    else if (!on && before) player.playOut();
  }, [on, player]);
  return <Stage spec={spec} player={player} texts={{ "Rød Efternavn 1": "medical break", Vægtklasse1: text }} visible={{}} />;
}

export function UrExtras({ ctx }: { ctx: GfxCtx }) {
  const { current, total } = roundOf(ctx);
  const medical = clock(timerRemaining(timerState(ctx, "medical"), ctx.now));
  return (
    <>
      <RoundMarks current={current} total={total} />
      <StrikesOverlay ctx={ctx} />
      <MedicalOverlay on={ctx.state.medical} text={medical} />
    </>
  );
}
